import { useState } from "react";
import axios from "axios";
import { CheckSquare, Download, FileText, Loader2, CheckCircle, ArrowRight, Briefcase, Target } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FreeResourcesPage = () => {
  const [selectedResource, setSelectedResource] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const resources = [
    {
      id: "business-registration",
      title: "Business Registration Checklist",
      description: "Everything you need to legally register your business - from choosing a structure to getting your EIN.",
      icon: Briefcase,
      items: [
        "Choose your business structure (LLC, S-Corp, Sole Prop)",
        "Search and reserve your business name",
        "Register with Secretary of State",
        "Apply for your EIN (Employer ID Number)",
        "Open a business bank account",
        "Get required business licenses",
        "Register for state/local taxes",
        "Set up your registered agent",
        "File initial compliance documents",
        "Create your operating agreement"
      ]
    },
    {
      id: "find-your-niche",
      title: "Find Your Monetizable Niche",
      description: "Discover what you're already doing for free that can become your income-generating purpose.",
      icon: Target,
      items: [
        "List 5 things people always ask you for help with",
        "Identify skills you'd do without getting paid",
        "Note what gives you energy (not drains you)",
        "Write down compliments you frequently receive",
        "List problems you naturally solve for others",
        "Identify your unique combination of experiences",
        "Research who would pay for this skill/service",
        "Calculate what others charge for similar services",
        "Define your ideal client/customer",
        "Create your first offer at a starter price"
      ]
    }
  ];

  const handleDownload = async () => {
    if (!email || !name) {
      toast.error("Please enter your name and email");
      return;
    }

    setLoading(true);
    try {
      // Save lead to database
      await axios.post(`${API_URL}/api/contact`, {
        name,
        email,
        message: `Downloaded free resource: ${selectedResource.title}`,
        service_interest: "free-resource"
      });

      setDownloaded(true);
      toast.success("Check your email! Your checklist is ready to download.");
    } catch (error) {
      console.error("Error:", error);
      // Still allow download even if save fails
      setDownloaded(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (downloaded && selectedResource) {
    return (
      <div data-testid="free-resources-page" className="min-h-screen bg-gray-50 py-20">
        <div className="max-w-4xl mx-auto px-6">
          {/* Success Header */}
          <div className="text-center mb-12">
            <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-[#1E3A5F] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Your Checklist is Ready!
            </h1>
            <p className="text-gray-600">
              Print this page or save it as a PDF for your records.
            </p>
          </div>

          {/* Printable Checklist */}
          <div className="bg-white rounded-xl shadow-lg p-8 print:shadow-none print:p-4" id="printable-checklist">
            <div className="border-b-2 border-[#9D9045] pb-6 mb-8">
              <div className="flex items-center gap-4 mb-4">
                <selectedResource.icon className="h-10 w-10 text-[#1E3A5F]" />
                <div>
                  <h2 className="text-2xl font-bold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {selectedResource.title}
                  </h2>
                  <p className="text-gray-600">{selectedResource.description}</p>
                </div>
              </div>
              <p className="text-sm text-gray-500">
                Provided by OMW Business & Financial Resources | omwbusiness@consultant.com | (877) 295-5905
              </p>
            </div>

            <div className="space-y-4">
              {selectedResource.items.map((item, index) => (
                <div key={index} className="flex items-start gap-4 p-4 border border-gray-200 rounded-lg hover:border-[#9D9045] transition-colors">
                  <div className="flex-shrink-0 h-6 w-6 border-2 border-[#1E3A5F] rounded mt-0.5" />
                  <div className="flex-1">
                    <span className="font-medium text-[#1E3A5F]">Step {index + 1}:</span>
                    <p className="text-gray-700">{item}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="bg-[#1E3A5F]/5 rounded-lg p-6">
                <h3 className="font-semibold text-[#1E3A5F] mb-2">Need Help With Your Business?</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Book a free discovery consultation with OMW Business & Financial Resources. We'll help you navigate every step of starting and growing your business.
                </p>
                <p className="text-[#9D9045] font-medium">
                  Call (877) 295-5905 or visit our website to schedule.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8 print:hidden">
            <Button 
              onClick={handlePrint}
              className="bg-[#1E3A5F] hover:bg-[#162c47] text-white px-8"
            >
              <Download className="mr-2 h-4 w-4" />
              Print / Save as PDF
            </Button>
            <Button 
              onClick={() => { setDownloaded(false); setSelectedResource(null); }}
              variant="outline"
              className="border-[#1E3A5F] text-[#1E3A5F]"
            >
              Get Another Checklist
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="free-resources-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1E3A5F] via-[#162c47] to-[#0f1e32] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <span className="text-[#9D9045] font-medium text-sm tracking-wider uppercase">Free Resources</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Free Business Checklists
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Download our free checklists to help you start and grow your business the right way.
          </p>
        </div>
      </section>

      {/* Resource Selection */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          {!selectedResource ? (
            <>
              <h2 className="text-2xl font-bold text-[#1E3A5F] text-center mb-12" style={{ fontFamily: 'Playfair Display, serif' }}>
                Choose Your Free Checklist
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {resources.map((resource) => (
                  <div 
                    key={resource.id}
                    onClick={() => setSelectedResource(resource)}
                    className="bg-white rounded-xl shadow-sm p-8 cursor-pointer hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-[#9D9045]"
                    data-testid={`resource-${resource.id}`}
                  >
                    <div className="h-16 w-16 rounded-full bg-[#1E3A5F]/10 flex items-center justify-center mb-6">
                      <resource.icon className="h-8 w-8 text-[#1E3A5F]" />
                    </div>
                    <h3 className="text-xl font-semibold text-[#1E3A5F] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {resource.title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {resource.description}
                    </p>
                    <div className="flex items-center text-[#9D9045] font-medium">
                      Get This Checklist <ArrowRight className="ml-2 h-4 w-4" />
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="max-w-lg mx-auto">
              <button 
                onClick={() => setSelectedResource(null)}
                className="text-gray-500 hover:text-[#1E3A5F] mb-6 flex items-center gap-2"
              >
                ← Back to resources
              </button>
              
              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="h-14 w-14 rounded-full bg-[#1E3A5F]/10 flex items-center justify-center">
                    <selectedResource.icon className="h-7 w-7 text-[#1E3A5F]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {selectedResource.title}
                    </h3>
                    <p className="text-sm text-gray-500">10-step actionable checklist</p>
                  </div>
                </div>

                <p className="text-gray-600 mb-6">
                  Enter your info below to get instant access to your free checklist.
                </p>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name">Your Name</Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="John Doe"
                      className="mt-1"
                      data-testid="resource-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="john@example.com"
                      className="mt-1"
                      data-testid="resource-email-input"
                    />
                  </div>
                  <Button 
                    onClick={handleDownload}
                    disabled={loading}
                    className="w-full bg-[#9D9045] hover:bg-[#8a7e3c] text-white py-6"
                    data-testid="download-checklist-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Download className="mr-2 h-5 w-5" />
                        Get My Free Checklist
                      </>
                    )}
                  </Button>
                </div>

                <p className="text-xs text-gray-500 mt-4 text-center">
                  We respect your privacy. No spam, ever.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#1E3A5F] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Need More Than a Checklist?
          </h2>
          <p className="text-gray-600 mb-8">
            Our consulting services can help you navigate every step of starting, growing, and managing your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/services">
              <Button className="bg-[#1E3A5F] hover:bg-[#162c47] text-white px-8">
                View Our Services
              </Button>
            </a>
            <a href="/contact">
              <Button variant="outline" className="border-[#9D9045] text-[#9D9045] hover:bg-[#9D9045] hover:text-white px-8">
                Schedule Free Consultation
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FreeResourcesPage;
